import { Navigate, Route, Routes } from "react-router-dom";
import { AlertTriangle, LoaderCircle } from "lucide-react";
import { AppShell } from "./components/AppShell";
import { Button, ErrorBanner } from "./components/Ui";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DashboardPage } from "./pages/DashboardPage";
import { EasyTecPage } from "./pages/EasyTecPage";
import { GoodsReceiptsPage } from "./pages/GoodsReceiptsPage";
import { InventoryPage } from "./pages/InventoryPage";
import { MasterDataPage } from "./pages/MasterDataPage";
import { PickupsPage } from "./pages/PickupsPage";
import { LoginPage } from "./pages/LoginPage";
import { UsersPage } from "./pages/UsersPage";
import { AccountPage } from "./pages/AccountPage";
import { SystemPage } from "./pages/SystemPage";
import { ScannerInboxPage } from "./pages/ScannerInboxPage";
import { OrderPage } from "./pages/OrderPage";
import { ReturnsPage } from "./pages/ReturnsPage";
import { DeveloperPage } from "./pages/DeveloperPage";
import { SuppliersPage } from "./pages/SuppliersPage";

function RoleOnly({ roles, children }: { roles: Array<"admin" | "dispatcher" | "warehouse">; children: React.ReactNode }) {
  const { profile } = useAuth();
  return profile && roles.includes(profile.role) ? children : <Navigate to="/" replace />;
}

function RoutedApp() {
  const { cloudConfigured, session, profile, loading, profileError, signOut } = useAuth();

  if (loading) {
    return <main className="boot-screen"><LoaderCircle className="spin" size={28} /><span>Lagersystem wird vorbereitet …</span></main>;
  }
  if (cloudConfigured && !session) return <LoginPage />;
  if (profileError) {
    return <main className="access-page"><ErrorBanner message={profileError} /><Button onClick={signOut}>Abmelden</Button></main>;
  }
  if (!profile || !profile.is_active) {
    return (
      <main className="access-page">
        <div className="access-page__icon"><AlertTriangle size={28} /></div>
        <h1>Konto noch nicht freigeschaltet</h1>
        <p>Ein Administrator muss diesem Konto zuerst eine Rolle und gegebenenfalls ein Lager zuweisen.</p>
        <Button onClick={signOut}>Abmelden</Button>
      </main>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="scanner-eingang" element={<ScannerInboxPage />} />
        <Route path="wareneingaenge" element={<GoodsReceiptsPage />} />
        <Route path="lagerbestand" element={<InventoryPage />} />
        <Route path="retouren" element={<ReturnsPage />} />
        <Route path="auftraege/:orderNumber" element={<OrderPage />} />
        <Route path="abholungen" element={<PickupsPage />} />
        <Route path="easytec" element={<RoleOnly roles={["admin", "dispatcher"]}><EasyTecPage /></RoleOnly>} />
        <Route path="stammdaten" element={<MasterDataPage />} />
        <Route path="lieferanten" element={<RoleOnly roles={["admin", "dispatcher"]}><SuppliersPage /></RoleOnly>} />
        <Route path="benutzer" element={<RoleOnly roles={["admin"]}><UsersPage /></RoleOnly>} />
        <Route path="system" element={<RoleOnly roles={["admin"]}><SystemPage /></RoleOnly>} />
        <Route path="entwickler" element={<RoleOnly roles={["admin"]}><DeveloperPage /></RoleOnly>} />
        <Route path="konto" element={<AccountPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return <AuthProvider><RoutedApp /></AuthProvider>;
}
